export type TipoDeConexaoDeImpressora = 'USB' | 'REDE'

export type LinguagemDeImpressora = 'ZPL' | 'TSPL' | 'EPL' | 'PPLA' | 'PPLB'

// A marca não determina a linguagem sozinha, mas na prática é como a pessoa identifica a impressora
// que tem na mão — daí a referência de fabricante em cada opção.
//
// `imprime` continua existindo mesmo com as três linguagens atendidas: o cadastro aceita a linguagem
// para o parque ficar mapeado, e uma linguagem nova entra aqui antes de existir renderer no backend.
// Enquanto as três estiverem em true, o aviso da lista e do diálogo simplesmente não aparece.
export const linguagensDeImpressora: {
	valor: LinguagemDeImpressora
	rotulo: string
	fabricantes: string
	imprime: boolean
	extensaoDeArquivo: string
}[] = [
	{ valor: 'ZPL', rotulo: 'ZPL', fabricantes: 'Zebra e compatíveis', imprime: true, extensaoDeArquivo: 'zpl' },
	{ valor: 'TSPL', rotulo: 'TSPL', fabricantes: 'TSC, Elgin, Argox', imprime: true, extensaoDeArquivo: 'tspl' },
	{ valor: 'EPL', rotulo: 'EPL', fabricantes: 'Zebra antiga', imprime: true, extensaoDeArquivo: 'epl' },
	// PPLA e PPLB não são variações uma da outra: a impressora Argox vem com o firmware de uma das
	// duas, e escolher a errada não imprime nada. Quem cadastra confere na etiqueta do equipamento.
	{ valor: 'PPLA', rotulo: 'PPLA', fabricantes: 'Argox (firmware PPLA), Datamax', imprime: true, extensaoDeArquivo: 'ppla' },
	{ valor: 'PPLB', rotulo: 'PPLB', fabricantes: 'Argox (firmware PPLB)', imprime: true, extensaoDeArquivo: 'pplb' },
]

export function extensaoDeArquivoDaLinguagem(linguagem: LinguagemDeImpressora): string {
	return linguagensDeImpressora.find(item => item.valor === linguagem)?.extensaoDeArquivo ?? 'txt'
}

export interface EstacaoDeImpressao {
	id: string
	identificador: string | null
	lojaId: string
	nome: string
	tipoDeConexao: TipoDeConexaoDeImpressora
	/**
	 * Nome da impressora instalada no Windows (conexão USB) ou ip:porta (conexão de rede).
	 * Quem interpreta é o agente local, conforme o tipoDeConexao.
	 */
	identificadorDaImpressora: string
	linguagem: LinguagemDeImpressora
	dpi: number
	/** Preenchidos pelo provisionamento da fila no backend — nunca digitados na tela. */
	queueUrl: string | null
	queueArn: string | null
	ativo: boolean
}

// 203 dpi é o padrão do parque de impressora térmica de etiqueta; 300 aparece em modelo de maior
// resolução e 600 é raro. Lista fechada em vez de campo livre porque errar o dpi não distorce um
// pouco, desloca todo o layout proporcionalmente.
export const dpisDeImpressoraTermica = [203, 300, 600]

export const DPI_PADRAO_DE_IMPRESSORA = 203

/**
 * Menor pedaço imprimível que o agente vai tratar como atômico. Vem pronto do backend — o frontend
 * nunca corta nem interpreta o conteúdo.
 */
export interface UnidadeDeImpressaoDeEtiqueta {
	indice: number
	quantidadeDeEtiquetas: number
	conteudo: string
}

/** O que virá a ser uma mensagem de fila. Por ora só é baixado para teste em impressora. */
export interface FatiaDeImpressaoDeEtiquetas {
	sequencia: number
	unidades: UnidadeDeImpressaoDeEtiqueta[]
	quantidadeDeEtiquetas: number
}

// Espelham EstacaoDeImpressaoValidator no backend, que é quem barra de verdade. Aqui servem para o
// maxlength do campo cortar antes de o usuário digitar demais e só descobrir no erro do salvar.
export const TAMANHO_MAXIMO_DO_NOME = 60
export const TAMANHO_MAXIMO_DO_IDENTIFICADOR = 100

export function criarEstacaoDeImpressao(lojaId: string): EstacaoDeImpressao {
	return {
		id: '',
		identificador: null,
		lojaId,
		nome: '',
		tipoDeConexao: 'USB',
		identificadorDaImpressora: '',
		linguagem: 'ZPL',
		dpi: DPI_PADRAO_DE_IMPRESSORA,
		queueUrl: null,
		queueArn: null,
		ativo: true,
	}
}

export type StatusDoJobDeImpressao = 'ENFILEIRANDO' | 'ENFILEIRADO' | 'CONCLUIDO' | 'PARCIAL' | 'FALHOU' | 'CANCELADO'

export type StatusDaFatiaDeImpressao = 'ENFILEIRADA' | 'ENTREGUE_AO_SPOOL' | 'IMPRESSA' | 'FALHOU' | 'DESCARTADA'

export interface FatiaDoJobDeImpressao {
	id: string
	jobDeImpressaoId: string
	sequencia: number
	quantidadeDeEtiquetas: number
	status: StatusDaFatiaDeImpressao
	tentativas: number
	erro: string | null
}

export interface JobDeImpressaoNoHistorico {
	id: string
	dataHora: string
	estacaoDeImpressaoId: string
	modeloDeEtiquetaId: string
	quantidadeDeEtiquetas: number
	quantidadeConfirmada: number
	totalDeFatias: number
	status: StatusDoJobDeImpressao
	fatias: FatiaDoJobDeImpressao[]
}

export interface ResultadoDeEnvioParaImpressao {
	jobId: string
	quantidadeDeEtiquetas: number
	totalDeFatias: number
}

export const situacoesDoJobDeImpressao: Record<StatusDoJobDeImpressao, { rotulo: string; cor: string }> = {
	ENFILEIRANDO: { rotulo: 'Enviando', cor: 'grey' },
	ENFILEIRADO: { rotulo: 'Na fila', cor: 'info' },
	CONCLUIDO: { rotulo: 'Impresso', cor: 'success' },
	PARCIAL: { rotulo: 'Saiu em parte', cor: 'warning' },
	FALHOU: { rotulo: 'Não saiu', cor: 'error' },
	CANCELADO: { rotulo: 'Cancelado', cor: 'grey' },
}

export interface HistoricoDeEmissaoDeEtiqueta {
	id: string
	dataHora: string
	lojaId: string
	lojaNome: string
	modeloId: string
	modeloDescricao: string
	estacaoId: string | null
	estacaoNome: string
	idTabelas: string[]
	parcelasPorTabela: Record<string, number>
	itens: {
		produtoId: string
		sku: string
		ean?: string
		nomeDoProduto: string
		quantidade: number
	}[]
}
